"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ParsedText } from "@/lib/text-parser";
import { parseText } from "@/lib/text-parser";

export type SelectionMode = "word" | "phrase" | "paragraph";

export type Screen = "home" | "reader";

export type FontFamily = "serif" | "sans" | "mono";

export type ReadingSettings = {
  fontSize: number; // px
  lineHeight: number; // multiplier
  fontFamily: FontFamily;
  marginWidth: number; // 0..40 (% horizontal padding)
  letterSpacing: number; // px
  justify: boolean; // justify text vs left-align
  hyphenate: boolean;
};

export type VocabularyEntry = {
  id: string; // word id at time of save
  word: string;
  context: string; // sentence text where it was saved
  translation: string | null;
  definition: string | null;
  savedAt: number;
};

type State = {
  // navigation
  screen: Screen;

  // source
  title: string;
  rawText: string;
  parsed: ParsedText | null;

  // selection
  selectionMode: SelectionMode;
  selectedWordId: string | null;
  selectedSentenceId: string | null;
  selectedParagraphId: string | null;
  // remember where the user clicked so we can re-anchor when cycling modes
  anchorWordId: string | null;

  // settings
  settings: ReadingSettings;

  // vocabulary
  vocabulary: VocabularyEntry[];

  // reader scroll position (memory of last position per session, not persisted)
  scrollTop: number;

  // ---- actions ----
  goHome: () => void;
  loadText: (raw: string, title?: string) => void;
  setScreen: (s: Screen) => void;

  setSelectionMode: (m: SelectionMode) => void;
  cycleSelectionMode: () => void;
  reverseCycleSelectionMode: () => void;
  selectWord: (wordId: string) => void;
  moveSelection: (dir: "next" | "prev") => void;
  clearSelection: () => void;

  setSettings: (patch: Partial<ReadingSettings>) => void;
  resetSettings: () => void;

  addToVocabulary: (entry: Omit<VocabularyEntry, "id" | "savedAt">) => void;
  removeFromVocabulary: (id: string) => void;
  updateVocabularyEntry: (id: string, patch: Partial<VocabularyEntry>) => void;
  inVocabulary: (word: string) => boolean;

  setScrollTop: (n: number) => void;
};

const DEFAULT_SETTINGS: ReadingSettings = {
  fontSize: 20,
  lineHeight: 1.7,
  fontFamily: "serif",
  marginWidth: 12,
  letterSpacing: 0,
  justify: false,
  hyphenate: false,
};

function deriveSelectionFromWord(
  parsed: ParsedText,
  wordId: string
): { sentenceId: string | null; paragraphId: string | null } {
  const w = parsed.words.find((x) => x.id === wordId);
  if (!w) return { sentenceId: null, paragraphId: null };
  return { sentenceId: w.sentenceId, paragraphId: w.paragraphId };
}

export const useStore = create<State>()(
  persist(
    (set, get) => ({
      screen: "home",
      title: "",
      rawText: "",
      parsed: null,

      selectionMode: "word",
      selectedWordId: null,
      selectedSentenceId: null,
      selectedParagraphId: null,
      anchorWordId: null,

      settings: DEFAULT_SETTINGS,
      vocabulary: [],

      scrollTop: 0,

      goHome: () => {
        set({ screen: "home", scrollTop: 0 });
      },

      setScreen: (s) => set({ screen: s }),

      loadText: (raw, title) => {
        const parsed = parseText(raw);
        const firstWord = parsed.words[0]?.id ?? null;
        const derived = firstWord
          ? deriveSelectionFromWord(parsed, firstWord)
          : { sentenceId: null, paragraphId: null };

        // If no title was provided, try to derive one from the first non-empty
        // line of the source. Fall back to a friendly default.
        let resolvedTitle = title?.trim() || "";
        if (!resolvedTitle) {
          const firstLine = raw
            .split(/\r?\n/)
            .map((l) => l.trim())
            .find((l) => l.length > 0);
          if (firstLine && firstLine.length <= 80) {
            resolvedTitle = firstLine;
          } else if (firstLine) {
            resolvedTitle = firstLine.slice(0, 77) + "…";
          } else {
            resolvedTitle = "Texto sem título";
          }
        }

        set({
          rawText: raw,
          parsed,
          title: resolvedTitle,
          screen: "reader",
          selectionMode: "word",
          selectedWordId: firstWord,
          selectedSentenceId: derived.sentenceId,
          selectedParagraphId: derived.paragraphId,
          anchorWordId: firstWord,
          scrollTop: 0,
        });
      },

      setSelectionMode: (m) => {
        const { parsed, selectedWordId, anchorWordId } = get();
        if (!parsed) return;
        // Always re-derive sentence/paragraph from the anchor (the most recently clicked word)
        const baseWordId = anchorWordId ?? selectedWordId;
        if (!baseWordId) return;
        const derived = deriveSelectionFromWord(parsed, baseWordId);
        set({
          selectionMode: m,
          selectedWordId: m === "word" ? baseWordId : selectedWordId,
          selectedSentenceId: derived.sentenceId,
          selectedParagraphId: derived.paragraphId,
        });
      },

      cycleSelectionMode: () => {
        const order: SelectionMode[] = ["word", "phrase", "paragraph"];
        const idx = order.indexOf(get().selectionMode);
        get().setSelectionMode(order[(idx + 1) % order.length]);
      },

      reverseCycleSelectionMode: () => {
        const order: SelectionMode[] = ["word", "phrase", "paragraph"];
        const idx = order.indexOf(get().selectionMode);
        get().setSelectionMode(order[(idx - 1 + order.length) % order.length]);
      },

      selectWord: (wordId) => {
        const { parsed } = get();
        if (!parsed) return;
        const derived = deriveSelectionFromWord(parsed, wordId);
        set({
          selectedWordId: wordId,
          selectedSentenceId: derived.sentenceId,
          selectedParagraphId: derived.paragraphId,
          anchorWordId: wordId,
          selectionMode: "word",
        });
      },

      moveSelection: (dir) => {
        const { parsed, selectionMode, selectedWordId, selectedSentenceId, selectedParagraphId } =
          get();
        if (!parsed) return;

        if (selectionMode === "word") {
          const cur = selectedWordId;
          if (!cur) return;
          const nextId =
            dir === "next"
              ? nextWordIdLocal(parsed, cur)
              : prevWordIdLocal(parsed, cur);
          if (nextId) {
            const derived = deriveSelectionFromWord(parsed, nextId);
            set({
              selectedWordId: nextId,
              selectedSentenceId: derived.sentenceId,
              selectedParagraphId: derived.paragraphId,
              anchorWordId: nextId,
            });
          }
        } else if (selectionMode === "phrase") {
          const cur = selectedSentenceId;
          if (!cur) return;
          const nextId =
            dir === "next"
              ? nextSentenceIdLocal(parsed, cur)
              : prevSentenceIdLocal(parsed, cur);
          if (nextId) {
            const s = parsed.sentences.find((x) => x.id === nextId);
            const firstWord = s?.wordIds[0] ?? null;
            set({
              selectedSentenceId: nextId,
              selectedParagraphId: s?.paragraphId ?? null,
              selectedWordId: firstWord,
              anchorWordId: firstWord,
            });
          }
        } else {
          const cur = selectedParagraphId;
          if (!cur) return;
          const nextId =
            dir === "next"
              ? nextParagraphIdLocal(parsed, cur)
              : prevParagraphIdLocal(parsed, cur);
          if (nextId) {
            const p = parsed.paragraphs.find((x) => x.id === nextId);
            const firstWord = p?.wordIds[0] ?? null;
            const firstSent = p?.sentenceIds[0] ?? null;
            set({
              selectedParagraphId: nextId,
              selectedSentenceId: firstSent,
              selectedWordId: firstWord,
              anchorWordId: firstWord,
            });
          }
        }
      },

      clearSelection: () =>
        set({
          selectedWordId: null,
          selectedSentenceId: null,
          selectedParagraphId: null,
          anchorWordId: null,
        }),

      setSettings: (patch) => set({ settings: { ...get().settings, ...patch } }),
      resetSettings: () => set({ settings: DEFAULT_SETTINGS }),

      addToVocabulary: (entry) => {
        const id = `${entry.word.toLowerCase()}-${Date.now()}`;
        const newEntry: VocabularyEntry = { ...entry, id, savedAt: Date.now() };
        // Avoid duplicates by word (case-insensitive)
        if (get().vocabulary.some((v) => v.word.toLowerCase() === entry.word.toLowerCase())) {
          return;
        }
        set({ vocabulary: [newEntry, ...get().vocabulary] });
      },

      removeFromVocabulary: (id) =>
        set({ vocabulary: get().vocabulary.filter((v) => v.id !== id) }),

      updateVocabularyEntry: (id, patch) =>
        set({
          vocabulary: get().vocabulary.map((v) => (v.id === id ? { ...v, ...patch } : v)),
        }),

      inVocabulary: (word) =>
        get().vocabulary.some((v) => v.word.toLowerCase() === word.toLowerCase()),

      setScrollTop: (n) => set({ scrollTop: n }),
    }),
    {
      name: "readfluent-store",
      // Only persist settings + vocabulary (NOT current text/selection — those are runtime)
      partialize: (s) => ({
        settings: s.settings,
        vocabulary: s.vocabulary,
      }),
    }
  )
);

// Local helpers — same as in text-parser but inlined to avoid circular imports in store hot paths.
function nextWordIdLocal(p: ParsedText, id: string): string | null {
  const i = p.words.findIndex((w) => w.id === id);
  if (i < 0 || i + 1 >= p.words.length) return null;
  return p.words[i + 1].id;
}
function prevWordIdLocal(p: ParsedText, id: string): string | null {
  const i = p.words.findIndex((w) => w.id === id);
  if (i <= 0) return null;
  return p.words[i - 1].id;
}
function nextSentenceIdLocal(p: ParsedText, id: string): string | null {
  const i = p.sentences.findIndex((s) => s.id === id);
  if (i < 0 || i + 1 >= p.sentences.length) return null;
  return p.sentences[i + 1].id;
}
function prevSentenceIdLocal(p: ParsedText, id: string): string | null {
  const i = p.sentences.findIndex((s) => s.id === id);
  if (i <= 0) return null;
  return p.sentences[i - 1].id;
}
function nextParagraphIdLocal(p: ParsedText, id: string): string | null {
  const i = p.paragraphs.findIndex((x) => x.id === id);
  if (i < 0 || i + 1 >= p.paragraphs.length) return null;
  return p.paragraphs[i + 1].id;
}
function prevParagraphIdLocal(p: ParsedText, id: string): string | null {
  const i = p.paragraphs.findIndex((x) => x.id === id);
  if (i <= 0) return null;
  return p.paragraphs[i - 1].id;
}
